import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import clsx from 'clsx';
import {
  DocumentTextIcon,
  PencilIcon,
  PlusIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  NumberedListIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { CharacterCount } from '@tiptap/extensions';
import { updateMeetingNotes } from '../meetings/actions';
import ProFeatureAction from './common/ProFeatureAction';


const CHARACTER_LIMIT = 2000;

function isEmptyDoc(json) {
  if (!json || !Array.isArray(json.content) || json.content.length === 0) return true;
  return json.content.every(
    (node) => node.type === 'paragraph' && (!node.content || node.content.length === 0)
  );
}

function ToolbarButton({ active, onClick, ariaLabel, children }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={clsx(
        'w-7 h-7 flex items-center justify-center rounded text-gray-1 hover:bg-gray-200',
        active && 'bg-gray-200 text-meet-black'
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor, state }) {
  if (!editor || !state) return null;

  return (
    <div className="flex items-center gap-1 px-5 py-1 bg-gray-2 border-t border-gray-200">
      <ToolbarButton
        ariaLabel="Bold"
        active={state.isBold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        ariaLabel="Italic"
        active={state.isItalic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        ariaLabel="Underline"
        active={state.isUnderline}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="w-4 h-4" />
      </ToolbarButton>
      <span className="w-px h-5 bg-gray-300 mx-1" aria-hidden="true" />
      <ToolbarButton
        ariaLabel="Bullet list"
        active={state.isBulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListBulletIcon className="w-5 h-5" />
      </ToolbarButton>
      <ToolbarButton
        ariaLabel="Numbered list"
        active={state.isOrderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <NumberedListIcon className="w-5 h-5" />
      </ToolbarButton>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-8 px-5 border-t border-gray-200">
      <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4">
        <DocumentTextIcon className="w-6 h-6 text-blue-1" aria-hidden="true" />
      </div>
      <p className="text-sm text-gray-1 max-w-md">
        <Trans i18nKey="meetings.notes.empty_body">
          You haven't added any notes for this meeting yet. Use the <strong className="font-bold text-meet-black">Add notes</strong> button above to capture decisions, action items, and anything else worth remembering.
        </Trans>
      </p>
    </div>
  );
}


export default function MeetingNotes({ meeting }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [savedNotes, setSavedNotes] = useState(() => meeting.notes ?? null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => isEmptyDoc(savedNotes));

  const editor = useEditor({
    extensions: [
      StarterKit,
      CharacterCount.configure({ limit: CHARACTER_LIMIT }),
    ],
    content: savedNotes,
    editable: false,
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => editor ? {
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isUnderline: editor.isActive('underline'),
      isBulletList: editor.isActive('bulletList'),
      isOrderedList: editor.isActive('orderedList'),
      charCount: editor.storage.characterCount.characters(),
    } : null,
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(isEditing);
    if (isEditing) {
      editor.chain().focus('end').run();
    }
  }, [editor, isEditing]);

  const handleEdit = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    setIsCollapsed(false);
    setIsEditing(true);
  };

  const handleHeaderClick = () => {
    if (isEditing) return;
    setIsCollapsed((c) => !c);
  };

  const handleCancel = () => {
    if (editor) {
      editor.commands.setContent(savedNotes);
    }
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!editor) {
      return;
    }

    const json = editor.getJSON();
    dispatch(updateMeetingNotes(meeting.local_key, json));
    setSavedNotes(json);
    setIsEditing(false);
  };

  const isEmpty = isEmptyDoc(savedNotes);
  const showBody = !isCollapsed;
  const charCount = editorState?.charCount ?? 0;

  let subtitle;
  if (isEditing) {
    subtitle = t('meetings.notes.editing');
  } else if (isEmpty) {
    subtitle = t('meetings.notes.no_notes_yet');
  } else if (meeting.updated_at) {
    subtitle = t('meetings.notes.edited_ago', { time: moment(meeting.updated_at).fromNow(true) });
  } else {
    subtitle = null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-5 overflow-hidden">
      {/* Header strip */}
      <div
        className={clsx(
          'flex items-center justify-between bg-gray-2 px-5 py-3 select-none transition-colors',
          !isEditing && 'cursor-pointer hover:bg-gray-100'
        )}
        onClick={handleHeaderClick}
      >
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-blue-1" aria-hidden="true" />
          <h5 className="text-sm font-bold">{t('meetings.notes.title')}</h5>
          {subtitle && (
            <span className="text-xs text-gray-1 ml-1">{subtitle}</span>
          )}
        </div>
        <div className="flex items-center gap-3 print:hidden">
          {!isEditing && (
            isEmpty ? (
              <ProFeatureAction
                onClick={handleEdit}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-blue-1 text-blue-1 hover:bg-blue-1 hover:text-white"
              >
                <PlusIcon className="w-3 h-3" aria-hidden="true" />
                {t('meetings.notes.add_notes')}
              </ProFeatureAction>
            ) : (
              <ProFeatureAction
                onClick={handleEdit}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-blue-1 text-blue-1 hover:bg-blue-1 hover:text-white"
              >
                <PencilIcon className="w-3 h-3" aria-hidden="true" />
                {t('common.edit')}
              </ProFeatureAction>
            )
          )}
          {!isEditing && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed((c) => !c);
              }}
              className="text-gray-1 hover:text-meet-black"
              aria-label={isCollapsed ? 'Expand notes' : 'Collapse notes'}
              aria-expanded={!isCollapsed}
            >
              <ChevronUpIcon
                className={clsx(
                  'w-5 h-5 transition-transform',
                  isCollapsed && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {showBody && (
        isEditing ? (
          <>
            <Toolbar editor={editor} state={editorState} />
            <EditorContent
              editor={editor}
              className="notes-editor is-editing px-5 py-4 border-t border-gray-200"
            />
            <div className="flex items-center justify-between px-5 py-2 bg-gray-2 border-t border-gray-200">
              <span
                className={clsx(
                  'text-xs',
                  charCount >= CHARACTER_LIMIT ? 'text-red-600' : 'text-gray-500'
                )}
              >
                {charCount} / {CHARACTER_LIMIT}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-blue-1 hover:opacity-75"
                  onClick={handleCancel}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-blue-1 text-white border border-blue-1 hover:opacity-75"
                  onClick={handleSave}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </>
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <EditorContent
            editor={editor}
            className="notes-editor px-5 py-4 border-t border-gray-200"
          />
        )
      )}
    </div>
  );
}
